import { FieldError } from '@ascleo/ui/components/ui/field'
import { cn } from '@ascleo/ui/lib/utils'
import { useStore } from '@tanstack/react-form'
import {
  ArrowRight,
  CircleCheck,
  ShieldCheck,
  ShieldPlus,
  User,
  UserRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getFormErrorMessage } from '../../../../../lib/forms/form-errors'
import { KioskTextField } from '../../../../../components/kiosk-field'
import { KioskDateOfBirthField } from '../../../../../components/kiosk-date-of-birth-field'
import { KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import { useStepPrimaryAction } from '../../../context'
import type { Insurance } from '../machine'
import type {
  CreatePatientStatus,
  SchedulerCreatePatientForm,
} from './patient-identification-forms'

const INSURANCE_OPTIONS: ReadonlyArray<{ id: Insurance; icon: LucideIcon }> = [
  { id: 'GKV', icon: ShieldCheck },
  { id: 'PKV', icon: ShieldPlus },
  { id: 'SELFPAY', icon: Wallet },
]

/**
 * Renders the scheduler first-visit form: Vorname, Nachname, Geburtsdatum in
 * the same order as the search (KO-71), an explicit insurance choice without
 * preselection, and "Weiter" in the footer.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * type SchedulerCreateModePanelProps = Parameters<typeof SchedulerCreateModePanel>[0]
 * ```
 */
export function SchedulerCreateModePanel({
  form,
  status,
}: {
  form: SchedulerCreatePatientForm
  status: CreatePatientStatus
}) {
  const { t } = useTranslation()
  const canSubmit = useStore(form.store, (state) => state.canSubmit)
  const values = useStore(form.store, (state) => state.values)
  const isCreating = status === 'creating'
  const complete =
    values.firstName.trim() !== '' &&
    values.lastName.trim() !== '' &&
    values.dateOfBirth.trim() !== '' &&
    values.insurance !== ''

  useStepPrimaryAction({
    label: t('common.next'),
    icon: ArrowRight,
    testId: 'frontdesk-scheduler-create-submit-btn',
    disabled: !canSubmit || !complete,
    busy: isCreating,
    onClick: () => void form.handleSubmit(),
  })

  const fieldError = (errors: readonly unknown[]) =>
    errors.length > 0 ? t(getFormErrorMessage(errors[0])) : undefined

  return (
    <div
      data-testid="frontdesk-scheduler-create-form"
      className="flex min-w-0 flex-col gap-5"
    >
      {status === 'failed' ? (
        <KioskStatePanel
          tone="error"
          testId="frontdesk-scheduler-create-error"
          title={t('self-service.identification.create.error-title')}
          body={t('self-service.identification.create.error-body')}
        />
      ) : null}

      <form.Field name="firstName">
        {(field) => (
          <KioskTextField
            id="scheduler-create-firstname"
            testId="frontdesk-scheduler-create-firstname-input"
            label={t('self-service.identification.lookup.first-name')}
            icon={User}
            required
            autoComplete="given-name"
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            disabled={isCreating}
            error={fieldError(field.state.meta.errors)}
          />
        )}
      </form.Field>

      <form.Field name="lastName">
        {(field) => (
          <KioskTextField
            id="scheduler-create-lastname"
            testId="frontdesk-scheduler-create-lastname-input"
            label={t('self-service.identification.lookup.last-name')}
            icon={UserRound}
            required
            autoComplete="family-name"
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            disabled={isCreating}
            error={fieldError(field.state.meta.errors)}
          />
        )}
      </form.Field>

      <form.Field name="dateOfBirth">
        {(field) => (
          <KioskDateOfBirthField
            id="scheduler-create-dob"
            testId="frontdesk-scheduler-create-dob-input"
            pickerTestId="frontdesk-scheduler-create-dob-picker-btn"
            label={t('self-service.identification.lookup.dob')}
            description={t('self-service.identification.lookup.dob-hint')}
            required
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            disabled={isCreating}
            error={fieldError(field.state.meta.errors)}
          />
        )}
      </form.Field>

      <form.Field name="insurance">
        {(field) => (
          <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
            <legend className="mb-3 text-lg font-semibold text-foreground">
              {t('self-service.identification.create.insurance-question')}
            </legend>
            <div role="radiogroup" className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
              {INSURANCE_OPTIONS.map(({ id, icon: Icon }) => {
                const selected = field.state.value === id
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`frontdesk-scheduler-create-insurance-${id.toLowerCase()}-btn`}
                    disabled={isCreating}
                    onClick={() => field.handleChange(id)}
                    className={cn(
                      'flex min-h-14 items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left text-lg font-semibold transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45 disabled:opacity-60',
                      selected
                        ? 'border-primary bg-primary-subtle text-primary-ink'
                        : 'border-border bg-card text-foreground hover:bg-foreground-soft',
                    )}
                  >
                    <Icon aria-hidden className="size-6 shrink-0" />
                    <span className="min-w-0 flex-1">{t(`common.insurance.${id}`)}</span>
                    {selected ? <CircleCheck aria-hidden className="size-6 shrink-0" /> : null}
                  </button>
                )
              })}
            </div>
            {field.state.meta.isTouched && field.state.meta.errors.length > 0 ? (
              <FieldError data-testid="frontdesk-scheduler-create-insurance-error">
                {fieldError(field.state.meta.errors)}
              </FieldError>
            ) : null}
          </fieldset>
        )}
      </form.Field>
    </div>
  )
}
